import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { NavHeaderLinkItem, NavLinkItemValue } from '@/gql/graphql'
import { resolveNavLink } from '@/utils/navigation'

type NavItem = Pick<NavHeaderLinkItem, 'link' | 'children' | 'label'>

export interface MobileNavProps {
  items: NavItem[]
  onNavigate: (url: string, isExternal: boolean) => void
  className?: string
}

export default function MobileNav({ items, onNavigate, className = '' }: MobileNavProps) {
  const [expandedLabel, setExpandedLabel] = useState<string | null>(null)

  const handleParentClick = (item: NavItem) => {
    const hasChildren = (item.children?.length ?? 0) > 0

    if (hasChildren) {
      setExpandedLabel((current) => (current === item.label ? null : item.label))
      return
    }

    const { url, isExternal } = resolveNavLink(item.link)
    onNavigate(url, isExternal)
  }

  const handleChildClick = (child: NavLinkItemValue) => {
    const { url, isExternal } = resolveNavLink(child)
    onNavigate(url, isExternal)
  }

  return (
    <ul className={`flex flex-col text-sm text-foreground ${className}`}>
      {items.map((item) => {
        const hasChildren = (item.children?.length ?? 0) > 0
        const isExpanded = expandedLabel === item.label

        return (
          <li key={item.label} className="border-b border-secondary/10 last:border-0">
            <button
              type="button"
              onClick={() => handleParentClick(item)}
              className="flex w-full items-center justify-between gap-3 py-4 pl-4 pr-3 text-left text-base font-semibold capitalize hover:bg-secondary/5 transition-colors cursor-pointer"
              aria-expanded={hasChildren ? isExpanded : undefined}
            >
              <span>{item.label}</span>
              {hasChildren && (
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-secondary transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                />
              )}
            </button>

            {hasChildren && isExpanded && (
              <ul className="flex flex-col pb-2 pl-4">
                {item.children!.map((child) => (
                  <li key={child.label}>
                    <button
                      type="button"
                      onClick={() => handleChildClick(child)}
                      className="flex w-full items-center py-3 pl-4 pr-3 text-left text-sm font-medium hover:bg-secondary/5 hover:text-primary-text transition-colors cursor-pointer"
                    >
                      {child.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        )
      })}
    </ul>
  )
}
